import { shouldShowContentField, isContentValue, formatContentLabel } from './adminContentFilter';
import {
  isInspectionCategoryField,
  expandInspectionCategoryFormValues,
  applyInspectionCategoryPayload,
} from './adminInspectionUtils';

const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && !Array.isArray(val);

export const getNested = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

export const setNested = (obj, path, value) => {
  const keys = path.split('.');
  const last = keys.pop();
  let cur = obj;
  keys.forEach((key) => {
    if (!cur[key] || typeof cur[key] !== 'object') {
      cur[key] = /^\d+$/.test(key) ? [] : {};
    }
    cur = cur[key];
  });
  cur[last] = value;
  return obj;
};

export const refValue = (field) => {
  if (!field) return '';
  if (typeof field === 'string') return field;
  return field._id || '';
};

export const refMultiValue = (fields) => {
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => (typeof f === 'object' && f?._id ? f._id : f)).filter(Boolean);
};

const parseKvLines = (raw) =>
  String(raw || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split('|').map((part) => part.trim());
      return {
        label: parts[0] || '',
        value: parts[1] || '',
        status: parts[2] || 'pending',
      };
    });

const formatKvLines = (items) =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const label = item?.label || '';
          const value = item?.value || '';
          const status = item?.status && item.status !== 'pending' ? ` | ${item.status}` : '';
          return `${label} | ${value}${status}`.trim();
        })
        .join('\n')
    : '';

const formatDateInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toISOString().slice(0, 10);
};

const inferPrimitiveField = (path, value) => {
  if (!isContentValue(path, value)) return null;

  if (typeof value === 'boolean') {
    return { name: path, label: formatContentLabel(path), type: 'boolean' };
  }
  if (typeof value === 'number') {
    return { name: path, label: formatContentLabel(path), type: 'number' };
  }
  if (typeof value === 'string' && value.length > 100) {
    return { name: path, label: formatContentLabel(path), type: 'textarea', rows: 4 };
  }
  return { name: path, label: formatContentLabel(path), type: 'text' };
};

/**
 * Turn nested JSON into simple admin text fields (content only).
 */
export const flattenJsonToSchema = (data, prefix = '', maxArrayItems = 24) => {
  const fields = [];
  if (data == null) return fields;

  if (!shouldShowContentField(prefix) && prefix) return fields;

  if (Array.isArray(data)) {
    if (data.length === 0) return fields;
    const count = Math.min(data.length, maxArrayItems);
    for (let i = 0; i < count; i += 1) {
      const item = data[i];
      const path = prefix ? `${prefix}.${i}` : String(i);
      if (!shouldShowContentField(path) && !path.match(/^(navItems|plans|fields|form\.fields|highlights|carousel|heroType)\.\d+$/)) {
        continue;
      }
      if (isPlainObject(item)) {
        const childFields = flattenJsonToSchema(item, path, maxArrayItems);
        if (childFields.length) {
          fields.push({
            name: `_section_${path}`,
            label: formatContentLabel(path),
            type: 'section',
          });
          fields.push(...childFields);
        }
      } else if (Array.isArray(item)) {
        /* skip nested arrays — not editable as flat fields */
      } else {
        const field = inferPrimitiveField(path, item ?? '');
        if (field) fields.push(field);
      }
    }
    return fields;
  }

  if (isPlainObject(data)) {
    Object.entries(data).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key;
      if (!shouldShowContentField(path)) return;

      if (Array.isArray(value)) {
        if (value.length === 0 || value.every((item) => typeof item === 'string')) {
          fields.push({
            name: path,
            label: formatContentLabel(path),
            type: 'lines',
            rows: Math.max(3, Math.min((value.length || 0) + 1, 10)),
            hint: 'One item per line',
          });
        } else {
          const childFields = flattenJsonToSchema(value, path, maxArrayItems);
          if (childFields.length) {
            fields.push({
              name: `_section_${path}`,
              label: formatContentLabel(path),
              type: 'section',
            });
            fields.push(...childFields);
          }
        }
      } else if (isPlainObject(value)) {
        const childFields = flattenJsonToSchema(value, path, maxArrayItems);
        if (childFields.length) {
          fields.push({
            name: `_section_${path}`,
            label: formatContentLabel(path),
            type: 'section',
          });
          fields.push(...childFields);
        }
      } else {
        const field = inferPrimitiveField(path, value);
        if (field) fields.push(field);
      }
    });
  }

  return fields;
};

export const arrayifyNumericKeys = (value) => {
  if (Array.isArray(value)) {
    return value.map(arrayifyNumericKeys);
  }
  if (!isPlainObject(value)) return value;

  const keys = Object.keys(value);
  if (keys.length > 0 && keys.every((key) => /^\d+$/.test(key))) {
    return keys
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => arrayifyNumericKeys(value[key]));
  }

  const result = {};
  Object.entries(value).forEach(([key, val]) => {
    result[key] = arrayifyNumericKeys(val);
  });
  return result;
};

/** Deep-merge form changes into existing doc; arrays merge by index (preserves untouched items). */
export const mergeFormPayload = (target, source) => {
  if (source === null || source === undefined) {
    return target;
  }
  if (Array.isArray(source)) {
    if (!Array.isArray(target)) {
      return source.map((item) => arrayifyNumericKeys(item));
    }
    const maxLen = Math.max(target.length, source.length);
    const result = [];
    for (let i = 0; i < maxLen; i += 1) {
      const srcItem = source[i];
      const tgtItem = target[i];
      if (srcItem === undefined) {
        if (tgtItem !== undefined) result[i] = tgtItem;
        continue;
      }
      if (isPlainObject(srcItem) && isPlainObject(tgtItem)) {
        result[i] = mergeFormPayload(tgtItem, srcItem);
      } else if (Array.isArray(srcItem) && isPlainObject(tgtItem)) {
        result[i] = tgtItem;
      } else if (srcItem !== null && srcItem !== '' && !Array.isArray(srcItem)) {
        result[i] = arrayifyNumericKeys(srcItem);
      } else if (tgtItem !== undefined) {
        result[i] = tgtItem;
      }
    }
    return result;
  }
  if (!isPlainObject(source)) return source;

  const output = isPlainObject(target) ? { ...target } : {};
  Object.entries(source).forEach(([key, value]) => {
    if (value === undefined) return;
    if (Array.isArray(value)) {
      output[key] = mergeFormPayload(output[key], value);
    } else if (isPlainObject(value) && isPlainObject(output[key])) {
      output[key] = mergeFormPayload(output[key], value);
    } else {
      output[key] = value;
    }
  });
  return output;
};

/** @deprecated use mergeFormPayload */
export const deepMerge = mergeFormPayload;

const NUMERIC_FLAG_FIELDS = new Set(['is_modify', 'is_project']);

export const normalizeFormValue = (field, val) => {
  if (field.type === 'boolean' && NUMERIC_FLAG_FIELDS.has(field.name)) {
    return Boolean(Number(val));
  }
  if (field.type === 'boolean') {
    return Boolean(val);
  }
  return val;
};

export const serializeFormValue = (field, val) => {
  if (field.type === 'boolean' && NUMERIC_FLAG_FIELDS.has(field.name)) {
    return val ? 1 : 0;
  }
  return val;
};

export const stripDocForMerge = (doc) => {
  if (!doc || typeof doc !== 'object') return {};
  const out = { ...doc };
  delete out._id;
  delete out.__v;
  delete out.createdAt;
  delete out.updatedAt;

  Object.entries(out).forEach(([key, val]) => {
    if (Array.isArray(val)) return;
    if (val && typeof val === 'object' && val._id) {
      out[key] = val._id;
    }
  });
  return out;
};

const shallowDocForMerge = (doc) => {
  if (!doc || typeof doc !== 'object') return {};
  const out = { ...doc };
  delete out._id;
  delete out.__v;
  delete out.createdAt;
  delete out.updatedAt;
  return out;
};

/** Site config + hero — keep nested content arrays (nav, carousel, buttons) intact */
export const prepareExistingForMerge = (doc, { siteConfig = false, preserveArrays = false } = {}) =>
  siteConfig || preserveArrays ? shallowDocForMerge(doc) : stripDocForMerge(doc);

export const validateForm = (values, schema) => {
  const errors = {};
  schema.forEach((field) => {
    if (field.type === 'section' || String(field.name).startsWith('_section_')) return;
    if (!field.required) return;
    const val = getNested(values, field.name) ?? values[field.name];
    const empty =
      val === undefined ||
      val === null ||
      val === '' ||
      (Array.isArray(val) && val.length === 0);
    if (empty) {
      errors[field.name] = `${field.label} is required`;
    }
    if (field.type === 'json') {
      const raw = getNested(values, field.name) ?? values[field.name];
      if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
        try {
          JSON.parse(String(raw));
        } catch {
          errors[field.name] = `${field.label} must be valid JSON`;
        }
      }
    }
  });
  return errors;
};

const isSkippableField = (field) =>
  field.type === 'section' || String(field.name).startsWith('_section_');

export const rowToFormValues = (row, schema) => {
  const values = {};
  schema.forEach((field) => {
    if (isSkippableField(field)) return;
    if (isInspectionCategoryField(field.name)) return;

    if (field.type === 'ref') {
      values[field.name] = refValue(getNested(row, field.name) ?? row[field.name]);
    } else if (field.type === 'refMulti') {
      values[field.name] = refMultiValue(getNested(row, field.name) ?? row[field.name]);
    } else if (field.type === 'lines') {
      const imgs = getNested(row, field.name) ?? row[field.name] ?? [];
      values[field.name] = Array.isArray(imgs) ? imgs.join('\n') : '';
    } else if (field.type === 'kvLines') {
      const items = getNested(row, field.name) ?? row[field.name] ?? [];
      values[field.name] = formatKvLines(items);
    } else if (field.type === 'json') {
      const raw = getNested(row, field.name) ?? row[field.name];
      if (raw == null || raw === '') {
        values[field.name] =
          field.default != null ? JSON.stringify(field.default, null, 2) : '[]';
      } else {
        values[field.name] = JSON.stringify(raw, null, 2);
      }
    } else if (field.type === 'date') {
      values[field.name] = formatDateInput(getNested(row, field.name) ?? row[field.name]);
    } else if (field.type === 'boolean') {
      const raw = getNested(row, field.name) ?? row[field.name];
      values[field.name] = normalizeFormValue(field, raw);
    } else {
      const val = getNested(row, field.name) ?? row[field.name];
      values[field.name] = val ?? field.default ?? '';
    }
  });
  expandInspectionCategoryFormValues(row, values);
  return values;
};

export const buildPayloadFromForm = (values, schema, mode) => {
  const payload = {};
  schema.forEach((field) => {
    if (isSkippableField(field)) return;
    if (isInspectionCategoryField(field.name)) return;
    if (field.showOn === 'create' && mode !== 'create') return;
    if (field.showOn === 'edit' && mode !== 'edit') return;
    if (field.type === 'password' && mode === 'edit' && !values[field.name]) return;

    let val = values[field.name];

    if (Array.isArray(val) && field.type !== 'refMulti' && field.type !== 'lines') {
      return;
    }

    if (field.type === 'number') {
      val = val === '' || val == null ? undefined : Number(val);
    } else if (field.type === 'boolean') {
      val = serializeFormValue(field, Boolean(val));
    } else if (field.type === 'lines') {
      val = String(val || '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (field.type === 'kvLines') {
      val = parseKvLines(val);
    } else if (field.type === 'json') {
      const raw = String(val || '').trim();
      if (!raw) {
        val = field.default ?? [];
      } else {
        val = JSON.parse(raw);
      }
    } else if (field.type === 'date') {
      val = val ? new Date(val).toISOString() : undefined;
    } else if (field.type === 'ref') {
      val = val || undefined;
    } else if (field.type === 'refMulti') {
      val = Array.isArray(val) ? val.filter(Boolean) : [];
    }

    if (val === undefined) return;

    if (field.name.includes('.')) {
      setNested(payload, field.name, val);
    } else {
      payload[field.name] = val;
    }
  });
  const hasCategoryFields = schema.some((field) => isInspectionCategoryField(field.name));
  if (hasCategoryFields) {
    applyInspectionCategoryPayload(payload, values);
  }
  return arrayifyNumericKeys(payload);
};

export const emptyFormValues = (schema, defaults = {}) => {
  const values = {};
  schema.forEach((field) => {
    if (isSkippableField(field)) return;
    if (isInspectionCategoryField(field.name)) return;

    const fromDefault = getNested(defaults, field.name);
    if (field.type === 'refMulti') {
      values[field.name] = fromDefault || [];
    } else if (field.type === 'boolean') {
      values[field.name] = fromDefault ?? field.default ?? false;
    } else if (field.type === 'lines') {
      const val = fromDefault ?? field.default ?? [];
      values[field.name] = Array.isArray(val) ? val.join('\n') : '';
    } else if (field.type === 'kvLines') {
      values[field.name] = formatKvLines(fromDefault ?? field.default ?? []);
    } else if (field.type === 'json') {
      const val = fromDefault ?? field.default ?? [];
      values[field.name] = JSON.stringify(val, null, 2);
    } else if (field.type === 'date') {
      values[field.name] = formatDateInput(fromDefault ?? field.default ?? '');
    } else {
      values[field.name] = fromDefault ?? field.default ?? '';
    }
  });
  expandInspectionCategoryFormValues(defaults, values);
  return values;
};
